import React, { useState } from 'react';
import { Info, Calculator, Linkedin } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const TypeActivite = {
  'Vente de marchandises': {
    social: 0.123,
    fiscal: 0.01,
    seuil: 188700,
    abattement: 0.71
  },
  'Prestation de services BIC': {
    social: 0.212,
    fiscal: 0.017,
    seuil: 77700,
    abattement: 0.50
  }
};

const SEUILS_VERSEMENT_LIBERATOIRE = {
  1: 27478,
  2: 54956,
  2.5: 68695,
  3: 82434
};

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title: string;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children, title }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        <div className="prose max-w-none">
          {children}
        </div>
      </div>
    </div>
  );
};

const calculPrevoyance = (age: number): number => {
  if (age < 30) return 1000;
  if (age < 40) return 1250;
  return 1500;
};

const calculRemunerationMaximale = (ca: number, depenses: number): number => {
  return Math.max(0, Math.floor((ca - depenses) / 1.4));
};

const formatMontant = (montant: number): string => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(montant);
};

const calculIR = (revenuImposable: number, nbParts: number): number => {
  const quotient = revenuImposable / nbParts;
  let impot = 0;

  if (quotient > 180294) impot += (quotient - 180294) * 0.45;
  if (quotient > 83823) impot += (Math.min(quotient, 180294) - 83823) * 0.41;
  if (quotient > 29315) impot += (Math.min(quotient, 83823) - 29315) * 0.30;
  if (quotient > 11497) impot += (Math.min(quotient, 29315) - 11497) * 0.11;

  return Math.round(impot * nbParts);
};

const Simulateur = () => {
  const [inputs, setInputs] = useState({
    typeActivite: 'Vente de marchandises',
    chiffreAffaires: '',
    remuneration: '',
    depenses: '',
    age: '',
    nbParts: '1',
    revenuFiscalReference: ''
  });
  const [resultats, setResultats] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState<string | null>(null);

  const handleCalcul = () => {
    const ca = Number(inputs.chiffreAffaires) || 0;
    const rem = Number(inputs.remuneration) || 0;
    const dep = Number(inputs.depenses) || 0;
    const age = Number(inputs.age) || 0;
    const nbParts = Number(inputs.nbParts) || 1;
    const rfr = inputs.revenuFiscalReference === '' ? null : Number(inputs.revenuFiscalReference);
    const prevoyance = calculPrevoyance(age);

    // SARL
    const calculSARL = () => {
      const chargesSocialesBase = rem <= 43992 ? rem * 0.45 : (43992 * 0.45) + ((rem - 43992) * 0.40);
      const chargesSociales = Math.max(1500, chargesSocialesBase); // Plancher de 1500€
      const beneficeImposable = ca - rem - chargesSociales - dep - prevoyance;
      const is = beneficeImposable > 0 ? 
        (beneficeImposable <= 42500 ? beneficeImposable * 0.15 : 42500 * 0.15 + (beneficeImposable - 42500) * 0.25) : 0;
      const baseIR = rem * 0.9;
      const ir = calculIR(baseIR, nbParts);
      const coutTotal = chargesSociales + is + ir + prevoyance;

      return {
        statut: 'SARL',
        chiffreAffaires: ca,
        depenses: dep,
        prevoyance,
        chargesSociales,
        is,
        ir,
        coutTotal,
        remunerationNette: rem - ir,
        resultatNet: beneficeImposable - is,
        details: {
          chargesSocialesCalc: `Minimum 1500€, sinon ${rem <= 43992 ? '45%' : '45% jusqu\'à 43 992€ puis 40%'} de la rémunération`,
          beneficeImposableCalc: `CA (${formatMontant(ca)}) - Rémunération (${formatMontant(rem)}) - Charges sociales (${formatMontant(chargesSociales)}) - Dépenses (${formatMontant(dep)}) - Prévoyance (${formatMontant(prevoyance)})`,
          isCalc: beneficeImposable > 0 ? '15% jusqu\'à 42 500€ puis 25%' : 'Pas d\'IS car pas de bénéfice',
          irCalc: `Calculé sur 90% de la rémunération avec ${nbParts} parts fiscales`
        }
      };
    };

    // SASU
    const calculSASU = () => {
      const chargesSociales = rem * 0.82;
      const beneficeImposable = ca - rem - chargesSociales - dep;
      const is = beneficeImposable > 0 ? 
        (beneficeImposable <= 42500 ? beneficeImposable * 0.15 : 42500 * 0.15 + (beneficeImposable - 42500) * 0.25) : 0;
      const baseIR = rem * 0.9;
      const ir = calculIR(baseIR, nbParts);
      const coutTotal = chargesSociales + is + ir;

      return {
        statut: 'SASU',
        chiffreAffaires: ca,
        depenses: dep,
        chargesSociales,
        is,
        ir,
        coutTotal,
        remunerationNette: rem - ir,
        resultatNet: beneficeImposable - is,
        details: {
          chargesSocialesCalc: '82% de la rémunération',
          beneficeImposableCalc: `CA (${formatMontant(ca)}) - Rémunération (${formatMontant(rem)}) - Charges sociales (${formatMontant(chargesSociales)}) - Dépenses (${formatMontant(dep)})`,
          isCalc: beneficeImposable > 0 ? '15% jusqu\'à 42 500€ puis 25%' : 'Pas d\'IS car pas de bénéfice',
          irCalc: `Calculé sur 90% de la rémunération avec ${nbParts} parts fiscales`
        }
      };
    };

    // AE
    const calculAE = () => {
      const taux = TypeActivite[inputs.typeActivite];
      
      if (ca > taux.seuil) {
        return { 
          statut: 'Auto-Entrepreneur', 
          depassementSeuil: true,
          chiffreAffaires: ca,
          seuilMax: taux.seuil
        };
      }

      const chargesSociales = ca * taux.social;
      const seuilVersementLiberatoire = SEUILS_VERSEMENT_LIBERATOIRE[nbParts];
      let ir;
      let versementLiberatoire = false;

      if (rfr !== null && rfr <= seuilVersementLiberatoire) {
        ir = ca * taux.fiscal;
        versementLiberatoire = true;
      } else {
        const baseImposable = ca * (1 - taux.abattement);
        ir = calculIR(baseImposable, nbParts);
      }

      const coutTotal = chargesSociales + ir;

      return {
        statut: 'Auto-Entrepreneur',
        chiffreAffaires: ca,
        depenses: dep,
        chargesSociales,
        ir,
        coutTotal,
        versementLiberatoire,
        tauxSocial: taux.social * 100,
        tauxFiscal: taux.fiscal * 100,
        details: {
          chargesSocialesCalc: `${(taux.social * 100).toFixed(1)}% du CA`,
          irCalc: versementLiberatoire 
            ? `Versement libératoire : ${(taux.fiscal * 100).toFixed(1)}% du CA` 
            : `Calculé sur ${((1 - taux.abattement) * 100).toFixed(0)}% du CA avec ${nbParts} parts fiscales`,
          seuilCA: `Seuil de CA : ${formatMontant(taux.seuil)}`,
          abattement: `Abattement forfaitaire : ${(taux.abattement * 100).toFixed(0)}%`
        }
      };
    };

    const resultatsAE = calculAE();
    const tousResultats = resultatsAE.depassementSeuil 
      ? [calculSARL(), calculSASU(), resultatsAE] 
      : [calculSARL(), calculSASU(), resultatsAE];

    setResultats(tousResultats.sort((a, b) => 
      a.depassementSeuil ? 1 : b.depassementSeuil ? -1 : a.coutTotal - b.coutTotal
    ));
  };

  const renderDetailsModal = (resultat: any) => {
    if (resultat.depassementSeuil) {
      return (
        <div className="space-y-4">
          <p>Le chiffre d'affaires de {formatMontant(resultat.chiffreAffaires)} dépasse le seuil autorisé de {formatMontant(resultat.seuilMax)} pour le statut auto-entrepreneur.</p>
          <p>Dans ce cas, il est obligatoire d'opter pour un autre statut juridique comme la SARL ou la SASU.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <h4 className="font-semibold">Détail des calculs</h4>
        
        <div className="space-y-2">
          <p className="font-medium">Charges sociales :</p>
          <p>{resultat.details.chargesSocialesCalc}</p>
          <p className="font-semibold">Total : {formatMontant(resultat.chargesSociales)}</p>
        </div>

        {resultat.prevoyance && (
          <div className="space-y-2">
            <p className="font-medium">Prévoyance :</p>
            <p>Montant forfaitaire basé sur l'âge ({inputs.age} ans)</p>
            <p className="font-semibold">Total : {formatMontant(resultat.prevoyance)}</p>
          </div>
        )}

        {resultat.details.beneficeImposableCalc && (
          <div className="space-y-2">
            <p className="font-medium">Calcul du bénéfice imposable :</p>
            <p>{resultat.details.beneficeImposableCalc}</p>
            <p className="font-semibold">Total : {formatMontant(resultat.resultatNet + resultat.is)}</p>
          </div>
        )}

        {resultat.is !== undefined && (
          <div className="space-y-2">
            <p className="font-medium">Impôt sur les Sociétés (IS) :</p>
            <p>{resultat.details.isCalc}</p>
            <p className="font-semibold">Total : {formatMontant(resultat.is)}</p>
          </div>
        )}

        <div className="space-y-2">
          <p className="font-medium">Impôt sur le Revenu (IR) :</p>
          <p>{resultat.details.irCalc}</p>
          <p className="font-semibold">Total : {formatMontant(resultat.ir)}</p>
        </div>

        {resultat.statut === 'Auto-Entrepreneur' && (
          <>
            <div className="space-y-2">
              <p className="font-medium">Seuils et abattements :</p>
              <p>{resultat.details.seuilCA}</p>
              <p>{resultat.details.abattement}</p>
            </div>
          </>
        )}

        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="font-medium text-blue-900">Coût total : {formatMontant(resultat.coutTotal)}</p>
          {resultat.statut !== 'Auto-Entrepreneur' && (
            <>
              <p className="text-blue-900">Rémunération nette : {formatMontant(resultat.remunerationNette)}</p>
              {resultat.resultatNet > 0 && (
                <p className="text-blue-900">Résultat net : {formatMontant(resultat.resultatNet)}</p>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-gray-50 py-4 sm:py-8">
      <div className="max-w-4xl mx-auto px-3 sm:px-4">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-4 sm:p-6">
            <div className="flex items-center gap-4">
              <div className="bg-white/10 rounded-full p-3">
                <Calculator className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1">Simulateur statut</h1>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-white/20 rounded-full text-xs text-blue-100">
                    à jour au 01-01-2025
                  </span>
                  <span className="px-2 py-1 bg-white/20 rounded-full text-xs text-blue-100">
                    Auto-entrepreneur / SARL / SAS
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            <div className="space-y-4 mb-8">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type d'activité
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={inputs.typeActivite}
                    onChange={e => setInputs({...inputs, typeActivite: e.target.value})}
                  >
                    {Object.keys(TypeActivite).map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Chiffre d'affaires annuel
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={inputs.chiffreAffaires}
                    onChange={e => setInputs({...inputs, chiffreAffaires: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dépenses annuelles
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={inputs.depenses}
                    onChange={e => setInputs({...inputs, depenses: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rémunération souhaitée
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={inputs.remuneration}
                    onChange={e => setInputs({...inputs, remuneration: e.target.value})}
                  />
                  {inputs.chiffreAffaires && inputs.depenses && (
                    <p className="mt-1 text-sm text-gray-500">
                      Maximum recommandé : {formatMontant(calculRemunerationMaximale(Number(inputs.chiffreAffaires), Number(inputs.depenses)))}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Âge
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={inputs.age}
                    onChange={e => setInputs({...inputs, age: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de parts fiscales
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={inputs.nbParts}
                    onChange={e => setInputs({...inputs, nbParts: e.target.value})}
                  >
                    <option value="1">1 (Personne seule)</option>
                    <option value="2">2 (Couple)</option>
                    <option value="2.5">2.5 (Couple + 1 enfant)</option>
                    <option value="3">3 (Couple + 2 enfants)</option>
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Revenu fiscal de référence (facultatif)
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={inputs.revenuFiscalReference}
                    onChange={e => setInputs({...inputs, revenuFiscalReference: e.target.value})}
                    placeholder="Pour le versement libératoire"
                  />
                </div>
              </div>

              <button
                onClick={handleCalcul}
                className="w-full bg-blue-600 text-white rounded-md py-3 px-4 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-lg font-medium"
              >
                Calculer
              </button>
            </div>

            {resultats && (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {resultats.map((resultat: any, index: number) => (
                    <div
                      key={resultat.statut}
                      className={`rounded-lg p-4 sm:p-6 ${
                        resultat.depassementSeuil
                          ? 'bg-red-50 border-2 border-red-500'
                          : index === 0
                          ? 'bg-blue-50 border-2 border-blue-500'
                          : 'bg-white border border-gray-200'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold text-gray-900">
                          {resultat.statut}
                        </h3>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setModalOpen(resultat.statut)}
                            className="text-gray-500 hover:text-gray-700"
                            title="Voir les détails"
                          >
                            <Info size={20} />
                          </button>
                          {resultat.depassementSeuil ? (
                            <span className="px-2 py-1 text-xs font-medium text-red-600 bg-red-100 rounded-full">
                              Seuil dépassé
                            </span>
                          ) : index === 0 && (
                            <span className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-full">
                              Optimal
                            </span>
                          )}
                        </div>
                      </div>

                      {resultat.depassementSeuil ? (
                        <p className="text-red-600">
                          Le chiffre d'affaires ({formatMontant(resultat.chiffreAffaires)}) dépasse 
                          le seuil autorisé ({formatMontant(resultat.seuilMax)})
                        </p>
                      ) : (
                        <div className="space-y-3 text-sm sm:text-base">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">CA</span>
                            <span className="font-medium">{formatMontant(resultat.chiffreAffaires)}</span>
                          </div>

                          {resultat.prevoyance && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Prévoyance</span>
                              <span className="font-medium">{formatMontant(resultat.prevoyance)}</span>
                            </div>
                          )}

                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Charges sociales</span>
                            <span className="font-medium">{formatMontant(resultat.chargesSociales)}</span>
                          </div>

                          {resultat.is !== undefined && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">IS</span>
                              <span className="font-medium">{formatMontant(resultat.is)}</span>
                            </div>
                          )}

                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">IR</span>
                            <span className="font-medium">{formatMontant(resultat.ir)}</span>
                          </div>

                          <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                            <span className="text-rose-600 font-semibold">Coût total</span>
                            <span className="text-rose-600 font-semibold">{formatMontant(resultat.coutTotal)}</span>
                          </div>

                          {resultat.versementLiberatoire && (
                            <div className="mt-3 p-3 bg-green-50 rounded-lg text-sm">
                              <p className="font-medium text-green-800">Versement libératoire applicable :</p>
                              <p className="text-green-700">• Charges sociales : {resultat.tauxSocial.toFixed(1)}%</p>
                              <p className="text-green-700">• Impôt : {resultat.tauxFiscal.toFixed(1)}%</p>
                            </div>
                          )}

                          {resultat.statut !== 'Auto-Entrepreneur' && (
                            <div className="pt-3 border-t border-gray-200">
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600">Net après impôts</span>
                                <span className="font-medium">{formatMontant(resultat.remunerationNette)}</span>
                              </div>
                              {resultat.resultatNet > 0 && (
                                <div className="flex justify-between items-center mt-2">
                                  <span className="text-gray-600">Résultat net</span>
                                  <span className="font-medium">{formatMontant(resultat.resultatNet)}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-8 bg-white p-4 sm:p-6 rounded-lg shadow overflow-x-auto">
                  <h3 className="text-xl font-semibold mb-4">Comparaison des coûts par statut</h3>
                  <div className="h-[400px] min-w-[600px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={resultats.map(r => ({
                        name: r.statut,
                        'Charges Sociales': r.chargesSociales,
                        'IS': r.is || 0,
                        'IR': r.ir,
                        'Prévoyance': r.prevoyance || 0
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis tickFormatter={(value) => formatMontant(value)} />
                        <Tooltip 
                          formatter={(value) => formatMontant(Number(value))}
                          labelStyle={{ color: '#374151' }}
                          contentStyle={{ backgroundColor: 'white', border: '1px solid #E5E7EB' }}
                        />
                        <Legend />
                        <Bar dataKey="Charges Sociales" fill="#60A5FA" />
                        <Bar dataKey="IS" fill="#34D399" />
                        <Bar dataKey="IR" fill="#F87171" />
                        <Bar dataKey="Prévoyance" fill="#A78BFA" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {modalOpen && resultats && (
        <Modal
          isOpen={!!modalOpen}
          onClose={() => setModalOpen(null)}
          title={`Détails des calculs - ${modalOpen}`}
        >
          {renderDetailsModal(resultats.find((r: any) => r.statut === modalOpen))}
        </Modal>
      )}
    </div>
  );
};

export default Simulateur;